<template>
  <v-dialog :value="true" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="1200" scrollable @input="onDialogInput">
    <v-card dark class="document-preview-dialog">
      <header class="preview-header">
        <div class="preview-heading">
          <div class="preview-label">Originalbeleg</div>
          <h2>{{ document.title || "Beleg ohne Titel" }}</h2>
          <p v-if="issuerName">Aktuell zugeordnet: {{ issuerName }}</p>
        </div>
        <v-btn outlined color="#65c98f" class="preview-action" @click="downloadDocument">
          <v-icon left small>mdi-download-outline</v-icon>
          Herunterladen
        </v-btn>
        <v-btn icon class="close-action" aria-label="Belegvorschau schliessen" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </header>

      <v-card-text class="preview-body">
        <div v-if="isApp" class="preview-message">
          <v-icon size="40" color="#65c98f">mdi-cellphone-arrow-down</v-icon>
          <h3>Beleg auf dem Gerät öffnen</h3>
          <p>Lade das Dokument herunter, um es in der App anzusehen.</p>
        </div>
        <div v-else-if="loading" class="preview-message" role="status" aria-live="polite">
          <v-progress-circular indeterminate color="#65c98f"></v-progress-circular>
          <p>Beleg wird geladen …</p>
        </div>
        <div v-else-if="error" class="preview-message" role="alert">
          <v-icon size="40" color="error">mdi-file-alert-outline</v-icon>
          <p>{{ error }}</p>
          <v-btn outlined color="#65c98f" class="preview-action" @click="loadPreview">Erneut laden</v-btn>
        </div>
        <div v-else-if="kind === 'image' && blobUrl" class="image-preview">
          <img :src="blobUrl" :alt="document.title || 'Originalbeleg'" />
        </div>
        <iframe v-else-if="kind === 'pdf' && blobUrl" :src="blobUrl" class="file-preview" title="Originalbeleg"></iframe>
        <iframe v-else-if="previewHtml" :srcdoc="previewHtml" sandbox="" class="file-preview" title="Word-Dokument"></iframe>
        <div v-else class="preview-message">
          <v-icon size="40" color="#65c98f">mdi-file-eye-outline</v-icon>
          <h3>Keine direkte Vorschau verfügbar</h3>
          <p>Lade das Dokument herunter, um dieses Dateiformat zu öffnen.</p>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api"
import mammoth from "mammoth"

export default {
  name: "DocumentPreviewDialog",
  props: {
    document: { type: Object, required: true },
    issuerName: { type: String, default: "" },
  },
  data() {
    return {
      loading: false,
      error: "",
      kind: "",
      blobUrl: null,
      previewHtml: "",
      requestId: 0,
    }
  },
  computed: {
    isApp() {
      return Boolean(window.flutter_inappwebview)
    },
  },
  watch: {
    "document._id": {
      immediate: true,
      handler() {
        this.loadPreview()
      },
    },
  },
  beforeDestroy() {
    this.requestId += 1
    this.clearPreview()
  },
  methods: {
    onDialogInput(open) {
      if (!open) this.close()
    },
    close() {
      this.requestId += 1
      this.clearPreview()
      this.$emit("close")
    },
    clearPreview() {
      if (this.blobUrl) URL.revokeObjectURL(this.blobUrl)
      this.blobUrl = null
      this.previewHtml = ""
      this.kind = ""
    },
    async loadPreview() {
      const requestId = ++this.requestId
      this.clearPreview()
      this.error = ""
      this.loading = !this.isApp
      if (this.isApp) return
      try {
        const blob = await api.documents.fetchDocumentSource(this.document._id)
        if (requestId !== this.requestId) return
        const type = (blob.type || "").split(";")[0].toLowerCase()
        if (type === "application/pdf" || type.startsWith("image/")) {
          this.kind = type === "application/pdf" ? "pdf" : "image"
          this.blobUrl = URL.createObjectURL(blob)
        } else if (type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
          const arrayBuffer = await blob.arrayBuffer()
          if (requestId !== this.requestId) return
          const result = await mammoth.convertToHtml({ arrayBuffer })
          if (requestId !== this.requestId) return
          // Word-Inhalt getrennt von der App und ohne aktive Skripte darstellen.
          this.previewHtml = `<!doctype html><html lang="de"><head><meta charset="utf-8">
            <meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data: blob:; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'">
            <style>html{font-size:16px}body{margin:0;padding:1.5rem;font-family:Arial,sans-serif;font-size:1rem;line-height:1.6;color:#222;background:#fff;overflow-wrap:anywhere}img{max-width:100%;height:auto}table{max-width:100%;border-collapse:collapse}td,th{padding:.5rem;border:1px solid #ddd}</style>
            </head><body>${result.value}</body></html>`
        }
      } catch (error) {
        if (requestId !== this.requestId) return
        this.error = "Der Beleg konnte nicht geladen werden. Bitte versuche es erneut."
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    downloadDocument() {
      api.documents.downloadDocument(this.document._id, this.document.title || "Beleg")
    },
  },
}
</script>

<style scoped>
.document-preview-dialog {
  display: flex;
  height: 90vh;
  flex-direction: column;
  background: #1c1c1e !important;
  color: #f5f5f5;
  font-size: 1rem;
}

.preview-header {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.preview-heading {
  min-width: 0;
  flex: 1 1 15rem;
  overflow-wrap: anywhere;
}

.preview-label {
  color: #65c98f;
  font-size: 0.75rem;
  font-weight: 700;
}

.preview-heading h2 {
  margin: 0.25rem 0;
  font-size: 1.25rem;
  line-height: 1.4;
}

.preview-heading p {
  margin: 0;
  color: #b6b6bc;
  font-size: 0.875rem;
  line-height: 1.5;
}

.preview-action {
  min-height: 2.75rem !important;
  font-size: 0.875rem !important;
  letter-spacing: 0;
  text-transform: none;
}

.close-action {
  min-width: 2.75rem;
  min-height: 2.75rem;
}

.preview-body {
  display: flex;
  min-height: 0;
  flex: 1 1 auto;
  padding: 0 !important;
  overflow: auto;
}

.file-preview {
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
}

.image-preview {
  width: 100%;
  padding: 1rem;
  overflow: auto;
  text-align: center;
}

.image-preview img {
  max-width: 100%;
  height: auto;
}

.preview-message {
  display: flex;
  width: 100%;
  min-height: 16rem;
  padding: 1.5rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  text-align: center;
}

.preview-message h3,
.preview-message p {
  max-width: 36rem;
  margin: 0;
  color: #f5f5f5;
  font-size: 1rem;
  line-height: 1.6;
}

@media (max-width: 599px) {
  .document-preview-dialog {
    height: 100%;
  }

  .preview-header {
    gap: 0.75rem;
    padding: 1rem;
  }

  .preview-heading {
    flex: 1 1 calc(100% - 4rem);
  }

  .close-action {
    order: 1;
  }

  .preview-action {
    order: 2;
  }
}
</style>
